import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import TableToolbar from './TableToolbar';
import { EmptyState } from '../Visual';

interface TableFlexSalesProps {
  title: string;
  children: ReactNode;
  loading?: boolean;
  error?: boolean;
  totalRows?: number;
  actions?: ReactNode;
}

const TableStyled = styled(Table)<{ hidebody?: number }>`
  width: 100%;
  border-collapse: separate;

  .MuiTableCell-root {
    border-bottom-color: ${({ theme }) => theme.palette.divider};
  }

  ${props =>
    props.hidebody &&
    `
  tbody {
    display: none;
  }
  `}
`;

const TableFlexSalesRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TableContainerShell = styled.div`
  flex: 1 1 auto;
  height: 100%;
  min-height: 360px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.spacing(0, 0, 2.5, 2.5)};
  background: ${({ theme }) => theme.customTokens.surfaces.dataGridHeaderGradient};
  box-shadow: ${({ theme }) =>
    theme.palette.mode === 'dark'
      ? `0 18px 50px ${alpha(theme.palette.common.black, 0.28)}`
      : `0 18px 50px ${alpha(theme.palette.text.primary, 0.08)}`};
  overflow: hidden;
`;

const TableContainerStyled = styled(TableContainer)`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: transparent;
`;

const OverlayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  padding: ${({ theme }) => theme.spacing(4)};
`;

const OverlayContent = styled.div`
  width: min(720px, 100%);
`;

export const TableBodyLoading = () => (
  <OverlayContainer>
    <CircularProgress />
  </OverlayContainer>
);
export const TableBodyError = () => {
  const { t } = useTranslation();
  return (
    <OverlayContainer>
      <Typography>{t('components.tableFlexSales.error')}</Typography>
    </OverlayContainer>
  );
};

export const TableBodyEmpty = () => {
  const { t } = useTranslation();

  return (
    <OverlayContainer>
      <OverlayContent>
        <EmptyState title={t('dataGridFlexSales.labels.noRows')} />
      </OverlayContent>
    </OverlayContainer>
  );
};

const TableFlexSales = ({
  title,
  children,
  loading,
  error,
  actions,
  totalRows,
}: TableFlexSalesProps) => {
  const isEmpty = !loading && !error && totalRows === 0;
  const hideBody = Number(Boolean(loading || error || isEmpty));
  return (
    <TableFlexSalesRoot data-tour="report-results">
      <TableToolbar title={title} actions={actions} />
      <TableContainerShell>
        <TableContainerStyled>
          <TableStyled hidebody={hideBody} size="small">
            {children}
          </TableStyled>
          {!loading && error && <TableBodyError />}
          {isEmpty && <TableBodyEmpty />}
          {loading && <TableBodyLoading />}
        </TableContainerStyled>
      </TableContainerShell>
    </TableFlexSalesRoot>
  );
};

export default TableFlexSales;
